import { findLocale } from './locale-catalog';

/**
 * Locale-aware formatting, built on the platform `Intl` API.
 *
 * Nothing here hard-codes a format. `MM/DD/YYYY` is correct in the United
 * States and wrong almost everywhere else; `1,234.56` is wrong in most of
 * Europe and in Brazil. Every date, number and currency in TABITA passes
 * through these functions.
 */

export function formatDate(
  value: Date | string,
  locale: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
  timeZone = 'UTC',
): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(date);
}

export function formatDateTime(value: Date | string, locale: string, timeZone = 'UTC'): string {
  return formatDate(value, locale, { dateStyle: 'medium', timeStyle: 'short' }, timeZone);
}

export function formatNumber(
  value: number,
  locale: string,
  options: Intl.NumberFormatOptions = {},
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Formats a monetary amount held in minor units.
 *
 * TABITA stores money as integers of the smallest currency unit — never as a
 * float. Binary floating point cannot represent 0.1 exactly, and a rounding
 * error in a church's giving ledger is not recoverable by an apology.
 *
 * The minor-unit exponent comes from `Intl`, so zero-decimal currencies (JPY)
 * and three-decimal currencies (BHD, KWD) are handled without a lookup table.
 */
export function formatMoney(minorUnits: number, currencyCode: string, locale: string): string {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  });
  const exponent = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  return formatter.format(minorUnits / 10 ** exponent);
}

/** Converts a decimal amount to minor units, rounding half away from zero. */
export function toMinorUnits(amount: number, currencyCode: string, locale = 'en'): number {
  const exponent =
    new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode }).resolvedOptions()
      .maximumFractionDigits ?? 2;
  return Math.round(amount * 10 ** exponent);
}

export function formatRelativeTime(
  value: Date | string,
  locale: string,
  // A default parameter on a pure formatting helper. Callers inside the domain
  // pass an injected clock's `now()`; the default exists for presentation code
  // where the reference point is genuinely "this render".
  // eslint-disable-next-line no-restricted-syntax
  now = new Date(),
): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const deltaSeconds = Math.round((date.getTime() - now.getTime()) / 1000);

  const thresholds: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1],
  ];

  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, seconds] of thresholds) {
    if (Math.abs(deltaSeconds) >= seconds || unit === 'second') {
      return formatter.format(Math.round(deltaSeconds / seconds), unit);
    }
  }
  return formatter.format(0, 'second');
}

/**
 * Selects the plural category for a count.
 *
 * English has two forms; Arabic has six and Russian has four. Any code that
 * writes `count === 1 ? 'person' : 'people'` is already broken in seven of the
 * ten launch locales.
 */
export function pluralCategory(count: number, locale: string): Intl.LDMLPluralRule {
  return new Intl.PluralRules(locale).select(count);
}

/**
 * A collator for sorting names in the reader's locale.
 *
 * `sensitivity: 'base'` ignores case and accents, so "José" and "Jose" sort
 * together in every locale. It does NOT flatten letters that a language treats
 * as distinct: in Spanish, ñ is its own letter between n and o, so "Muñoz"
 * sorts after "Munoz" — which is correct, and is what a Spanish reader expects
 * from an alphabetical list.
 *
 * Sorting and searching are therefore different problems. A reader typing
 * "Munoz" still needs to find "Muñoz", and that is handled by PostgreSQL's
 * `unaccent` extension in the search path, not here.
 *
 * `numeric` keeps "Campus 2" before "Campus 10".
 */
export function createCollator(locale: string): Intl.Collator {
  return new Intl.Collator(locale, { sensitivity: 'base', numeric: true, usage: 'sort' });
}

/** Formats a person's name for display, honouring their preferred name. */
export function formatPersonName(person: {
  firstName: string;
  lastName: string;
  preferredName?: string | null;
}): string {
  return `${person.preferredName?.trim() || person.firstName} ${person.lastName}`.trim();
}

/** The `"Last, Preferred"` key used for directory sorting. */
export function buildSortName(person: {
  firstName: string;
  lastName: string;
  preferredName?: string | null;
}): string {
  return `${person.lastName}, ${person.preferredName?.trim() || person.firstName}`.trim();
}

export function localeDirection(code: string): 'ltr' | 'rtl' {
  return findLocale(code)?.direction ?? 'ltr';
}
